import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import HistoryChart, { hasSufficientData } from './HistoryChart';
import { useLabHistory } from '../../../../../hooks/api/useLabHistory';
import { APITypes } from '../../../../../primary-record-types';
import getDateString from '../../../../../utils/getDateString';
import {
    dedupeLabs,
    getLabValueValueDisplay,
    getReferenceRangeDisplay,
    isHighValue,
    isLowValue,
    splitLabsByUnits,
} from '../../../../../utils/medicalDataUtils';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import Text from '../../../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { mainContentPadding } from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import RichTable from '../../../../v2BaseComponents/RichTable';
import { TableConfig } from '../../../../v2BaseComponents/RichTable/types';
import { useRichTableState } from '../../../../v2BaseComponents/RichTable/useRichTableState';

const intl = getIntl('lab-history-screen', {
    high: 'High',
    low: 'Low',
    labsOn: '"{labName}" Labs',
    date: 'Date',
    value: 'Value',
    status: 'High/Low',
    referenceRange: 'Reference Range',
    multipleUnitsHeader: 'Multiple Units Detected',
    multipleUnitsDescription:
        'This lab has multiple units detected. Distinct units are displayed in separate graphs above.',
    header: '{name}',
    source: 'Source',
    history: 'History',
    trends: 'Trends',
    labHistory: 'Lab History',
});

export type LabHistoryScreenOwnProps = {};

export type LabHistoryScreenProps = LabHistoryScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'lab-history'>;
};

type TableData = {
    date: string;
    value: string;
    status: string;
    referenceRange: string;
    source: string | null;
};

const getTableData = (
    lab: APITypes.medicalDataService.PrimaryRecordLabWithHealthSystemName,
    code: string,
): TableData => {
    const targetLabValue =
        lab.values.find((value) => value.componentCoding?.code === code) ||
        (lab.labType.code === code && lab.values.find((_) => !_.componentCoding));
    if (!targetLabValue) {
        return {
            date: getDateString(lab.dateTime, 'date-time'),
            value: '',
            status: '',
            referenceRange: '',
            source: null,
        };
    }

    return {
        date: getDateString(lab.dateTime, 'date-time'),
        value: getLabValueValueDisplay(targetLabValue),
        status: isHighValue(targetLabValue) ? intl('high') : isLowValue(targetLabValue) ? intl('low') : '',
        referenceRange: getReferenceRangeDisplay(targetLabValue) || '',
        source: lab.healthSystemName || null,
    };
};

const tableConfig: TableConfig<TableData> = {
    columns: [
        {
            label: intl('date'),
            columnKey: 'date',
            render: (data) => data.date,
        },
        {
            label: intl('value'),
            columnKey: 'value',
            render: (data) => data.value,
        },
        {
            label: intl('status'),
            columnKey: 'status',
            render: (data) => data.status,
        },
        {
            label: intl('referenceRange'),
            columnKey: 'referenceRange',
            render: (data) => data.referenceRange,
        },
        {
            label: intl('source'),
            columnKey: 'source',
            render: (data) => data.source || '',
        },
    ],
    getRowKey: (data) => JSON.stringify(data),
};

const LabHistoryScreen: React.FC<LabHistoryScreenProps> = (props) => {
    const [tableState, setTableState] = useRichTableState();
    const { isLoadingLabHistory, labHistory } = useLabHistory({
        medicalProfileId: props.route.params.profileId,
        code: props.route.params.labCode,
    });

    const dedupedLabHistory = useMemo(() => dedupeLabs(labHistory.labHistory), [labHistory]);

    const labTableData = useMemo<TableData[]>(
        () =>
            dedupedLabHistory
                .map((lab) => getTableData(lab, props.route.params.labCode))
                .sort((a, b) => {
                    return new Date(b.date).getTime() - new Date(a.date).getTime();
                }),
        [dedupedLabHistory, props.route.params.labCode],
    );

    const name = useMemo(
        () => labHistory.labHistory.map((_) => _.labType).filter(Boolean)[0]?.display || '',
        [labHistory.labHistory],
    );
    useSetHeaderTitle(name || intl('labHistory'));

    const splitByUnits = useMemo(
        () =>
            splitLabsByUnits(labHistory.labHistory, props.route.params.labCode).filter((_) =>
                hasSufficientData(_, props.route.params.labCode),
            ),
        [labHistory, props.route.params.labCode],
    );

    return (
        <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder>
            <AnimateLoadedContent isLoading={isLoadingLabHistory}>
                {() => (
                    <ActionButtonLayout scrollable whiteBackground>
                        <Stack vertical space={contentStackSpacing} style={{ padding: mainContentPadding }}>
                            {splitByUnits.map((labs, index) => (
                                <HistoryChart key={index} labs={labs} code={props.route.params.labCode} />
                            ))}
                            <Text variant="h3">{name}</Text>
                            <Text variant="h4">{intl('history')}</Text>
                            <RichTable
                                data={labTableData}
                                minWidth={'100%'}
                                state={tableState}
                                config={tableConfig}
                                onStateChange={setTableState}
                                noPaper
                                noPaperPadding
                            />
                        </Stack>
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
        </ProfileHeaderContainer>
    );
};

export default React.memo(LabHistoryScreen);
